import qs from 'query-string'

import type { HttpTransport } from '@mangovoice/api-client'
import type { MangoApiClient } from '@mangovoice/mango-api-client'

import { HospitalityRoomStatus } from '../constants/roomStatuses'
import type {
  BulkCreatePayload,
  BulkCreateResult,
  BulkDeleteResult,
  BulkExtensionHospitalityUpdateResult,
  CallEntry,
  CallHistoryEntry,
  ClearAllVoicemailsResult,
  CreateWakeupPayload,
  EmergencyCall,
  ExtensionLine,
  ListRoomsParams,
  ListVoicemailsParams,
  ListWakeupParams,
  PaginatedResult,
  PbxHospitalitySettings,
  ResetResult,
  Room,
  RoomResetSchedulePayload,
  RoomResetScheduleResult,
  Voicemail,
  WakeupCall,
} from '../types'
import { mapCallEntryToHistoryEntry } from '../types'
import type { ApiResponse } from '../types/api'
import {
  type DeletePbxWakeupCallsQuery,
  HOTELS_GATEWAY_ENDPOINTS,
  type ListPbxRoomsQuery,
  type ListPbxWakeupCallsQuery,
  NON_PROXIED_ENDPOINTS,
} from './endpoints'
import {
  BULK_CREATE_WAKEUP_CALLS_MAX,
  chunk,
  type GatewayBulkCreateResponse,
  type GatewayBulkUpdateResponse,
  type GatewayCreateWakeupCallRequest,
  type GatewayDeleteResponse,
  type GatewayPbx,
  type GatewayRoomList,
  type GatewayUpdatePbxRequest,
  type GatewayUpdateRoomRequest,
  type GatewayWakeupCall,
  type GatewayWakeupCallList,
  normalizeWakeupCall,
} from './gatewayShapes'

/**
 * Transports used by the hotels client.
 *
 * - `gatewayTransport` — base URL is the api-gateway host
 *   (`customer-api.mangovoice.com`). Every hotels-service endpoint goes here.
 * - `mangoTransport` — base URL is mango-api. Used for the endpoints with no
 *   hotels-service equivalent (calls, voicemails, extension flags/lines).
 *
 * The single-transport form is kept for consumers that hand over one
 * pre-configured transport (e.g. `mangoApi.hotels`); in that case gateway
 * paths are issued through that same transport.
 */
export type HotelsApiConfig =
  | MangoApiClient['hotels']
  | {
      gatewayTransport: HttpTransport
      mangoTransport: HttpTransport
    }

function getTransports(config: HotelsApiConfig): {
  mango: HttpTransport
  gateway: HttpTransport
} {
  if ('gatewayTransport' in config) {
    return {
      mango: config.mangoTransport,
      gateway: config.gatewayTransport,
    }
  }
  const transport = config as HttpTransport
  return { mango: transport, gateway: transport }
}

export const createHotelsApi = (config: HotelsApiConfig) => {
  const { mango, gateway } = getTransports(config)

  const listWakeupCallsPage = async (
    params: ListWakeupParams,
  ): Promise<ApiResponse<PaginatedResult<WakeupCall>>> => {
    // `ordering` has no hotels-service equivalent — the service returns
    // wake-up calls ordered by timestamp, and consumers sort locally.
    const query: ListPbxWakeupCallsQuery = {
      extension_uuid: params.extension_id,
      include_deleted: params.include_deleted,
      limit: params.limit,
      offset: params.offset,
    }
    const search = qs.stringify(query, {
      skipNull: true,
      skipEmptyString: true,
    })
    const url = `${HOTELS_GATEWAY_ENDPOINTS.PBX_WAKEUP_CALLS(params.pbx_id)}${
      search ? `?${search}` : ''
    }`
    const response = await gateway.get<GatewayWakeupCallList>(url)
    if (!response.ok)
      return response as unknown as ApiResponse<PaginatedResult<WakeupCall>>
    const data = response.data as GatewayWakeupCallList
    return {
      ...response,
      data: {
        ...data,
        results: (data.results ?? []).map(normalizeWakeupCall),
      },
    } as ApiResponse<PaginatedResult<WakeupCall>>
  }

  return {
    // --- Rooms (hotels-service via api-gateway) ---

    listRooms: async (
      params: ListRoomsParams,
    ): Promise<ApiResponse<PaginatedResult<Room>>> => {
      // `overview` has no hotels-service equivalent — `wakeup_calls_count` is
      // always included in the room payload.
      const query: ListPbxRoomsQuery = {
        limit: params.limit,
        offset: params.offset,
      }
      const search = qs.stringify(query, {
        skipNull: true,
        skipEmptyString: true,
      })
      const url = `${HOTELS_GATEWAY_ENDPOINTS.PBX_ROOMS(params.pbx_uuid)}${
        search ? `?${search}` : ''
      }`
      return gateway.get<GatewayRoomList>(url) as Promise<
        ApiResponse<PaginatedResult<Room>>
      >
    },

    getRoom: async (extensionUuid: string): Promise<ApiResponse<Room>> => {
      const response = await gateway.get<Room>(
        HOTELS_GATEWAY_ENDPOINTS.ROOM_DETAIL(extensionUuid),
      )
      if (response.ok && response.data) {
        const room = response.data
        room.status_code = (room.status_code ??
          room.status) as Room['status_code']
      }
      return response
    },

    updateRoomStatus: async (
      extensionUuid: string,
      status: number,
    ): Promise<ApiResponse<Room>> => {
      const body: GatewayUpdateRoomRequest = { status }
      return gateway.patch<Room>(
        HOTELS_GATEWAY_ENDPOINTS.ROOM_DETAIL(extensionUuid),
        body,
      )
    },

    /**
     * Reset every room on the PBX to DIRTY. hotels-service exposes this as a
     * bulk PATCH over the PBX's rooms rather than a dedicated action.
     */
    resetAllRooms: async (
      pbxUuid: string,
    ): Promise<ApiResponse<ResetResult>> => {
      const body: GatewayUpdateRoomRequest = {
        status: HospitalityRoomStatus.DIRTY,
      }
      const response = await gateway.patch<GatewayBulkUpdateResponse>(
        HOTELS_GATEWAY_ENDPOINTS.PBX_ROOMS(pbxUuid),
        body,
      )
      if (!response.ok) return response as unknown as ApiResponse<ResetResult>
      const data = response.data as GatewayBulkUpdateResponse | undefined
      return {
        ...response,
        data: {
          success: data?.success ?? true,
          reset_count: data?.updated ?? 0,
        },
      } as ApiResponse<ResetResult>
    },

    // --- Wake-Up Calls (hotels-service via api-gateway) ---

    listWakeupCalls: listWakeupCallsPage,

    createWakeupCall: async (
      data: CreateWakeupPayload,
    ): Promise<ApiResponse<WakeupCall>> => {
      const body: GatewayCreateWakeupCallRequest = data
      const response = await gateway.post<GatewayWakeupCall>(
        HOTELS_GATEWAY_ENDPOINTS.PBX_WAKEUP_CALLS(data.pbx_uuid),
        body,
      )
      if (!response.ok) return response as unknown as ApiResponse<WakeupCall>
      return {
        ...response,
        data: normalizeWakeupCall(response.data as GatewayWakeupCall),
      } as ApiResponse<WakeupCall>
    },

    /**
     * Bulk create wake-up calls. hotels-service accepts an array body on the
     * PBX-scoped collection, capped at
     * {@link BULK_CREATE_WAKEUP_CALLS_MAX} per request — larger batches are
     * split and the results merged.
     */
    bulkCreateWakeupCalls: async (
      data: BulkCreatePayload,
    ): Promise<ApiResponse<BulkCreateResult>> => {
      const calls: GatewayCreateWakeupCallRequest[] = data.wakeup_calls ?? []
      const pbxUuid = calls[0]?.pbx_uuid ?? ''
      const batches = chunk(calls, BULK_CREATE_WAKEUP_CALLS_MAX)

      let created = 0
      const wakeupCalls: WakeupCall[] = []
      let lastResponse: ApiResponse<GatewayBulkCreateResponse> | undefined

      for (const batch of batches) {
        const response = await gateway.post<GatewayBulkCreateResponse>(
          HOTELS_GATEWAY_ENDPOINTS.PBX_WAKEUP_CALLS(pbxUuid),
          batch,
        )
        lastResponse = response as ApiResponse<GatewayBulkCreateResponse>
        if (!response.ok) {
          return response as unknown as ApiResponse<BulkCreateResult>
        }
        const body = response.data as GatewayBulkCreateResponse | undefined
        const batchCalls = body?.wakeup_calls ?? []
        created += body?.created ?? batchCalls.length
        wakeupCalls.push(...batchCalls.map(normalizeWakeupCall))
      }

      return {
        ...(lastResponse ?? { ok: true, status: 201 }),
        data: {
          success: true,
          created_count: created,
          wakeup_calls: wakeupCalls,
        },
      } as ApiResponse<BulkCreateResult>
    },

    deleteWakeupCall: async (
      id: number | string,
    ): Promise<ApiResponse<void>> => {
      return gateway.delete<void>(
        HOTELS_GATEWAY_ENDPOINTS.WAKEUP_CALL_DETAIL(String(id)),
      )
    },

    /**
     * Cancel every wake-up call for a room. hotels-service takes the target as
     * a query parameter rather than a request body.
     */
    bulkDeleteWakeupCalls: async (
      extensionUuid: string,
      pbxUuid: string,
    ): Promise<ApiResponse<BulkDeleteResult>> => {
      const query: DeletePbxWakeupCallsQuery = {
        extension_uuid: extensionUuid,
      }
      const url = `${HOTELS_GATEWAY_ENDPOINTS.PBX_WAKEUP_CALLS(
        pbxUuid,
      )}?${qs.stringify(query, { skipNull: true, skipEmptyString: true })}`
      const response = await gateway.delete<GatewayDeleteResponse>(url)
      if (!response.ok)
        return response as unknown as ApiResponse<BulkDeleteResult>
      const data = response.data as GatewayDeleteResponse | undefined
      return {
        ...response,
        data: {
          success: data?.success ?? true,
          canceled_count: data?.deleted ?? 0,
        },
      } as ApiResponse<BulkDeleteResult>
    },

    // --- Call History (non-proxied, always mango-api) ---

    listCallHistory: async (
      pbxId: number,
    ): Promise<ApiResponse<PaginatedResult<CallHistoryEntry>>> => {
      const url = `${NON_PROXIED_ENDPOINTS.CALLS}?${qs.stringify(
        {
          pbx_ids: String(pbxId),
          directions: 'inbound',
          types: 'standard',
          limit: 100,
          ordering: '-started_at',
        },
        { skipNull: true },
      )}`
      const response = await mango.get<PaginatedResult<CallEntry>>(url)
      if (!response.ok)
        return response as ApiResponse<PaginatedResult<CallHistoryEntry>>
      const data = response.data as PaginatedResult<CallEntry>
      return {
        ...response,
        data: {
          ...data,
          results: data.results.map(mapCallEntryToHistoryEntry),
        },
      } as ApiResponse<PaginatedResult<CallHistoryEntry>>
    },

    listEmergencyCalls: async (
      pbxId: number,
    ): Promise<ApiResponse<EmergencyCall[]>> => {
      // mango-api does the emergency-number matching (region-aware), the
      // 7-day window, and the 10-row cap server-side, returning rows already
      // shaped as EmergencyCall.
      const url = `${NON_PROXIED_ENDPOINTS.CALL_HISTORY_EMERGENCY}?${qs.stringify(
        { pbx_id: String(pbxId) },
        { skipNull: true },
      )}`
      return mango.get<EmergencyCall[]>(url)
    },

    // --- Voicemails (non-proxied, always mango-api) ---

    listVoicemails: async (
      params: ListVoicemailsParams,
    ): Promise<ApiResponse<PaginatedResult<Voicemail>>> => {
      const { room_id, ...queryParams } = params
      const url = `${NON_PROXIED_ENDPOINTS.ROOM_VOICEMAILS}?${qs.stringify(
        { room_id, ...queryParams },
        {
          skipNull: true,
          skipEmptyString: true,
        },
      )}`
      return mango.get<PaginatedResult<Voicemail>>(url)
    },

    clearAllVoicemails: async (
      roomId: number,
      pbxId: number,
    ): Promise<ApiResponse<ClearAllVoicemailsResult>> => {
      const url = `${NON_PROXIED_ENDPOINTS.ROOM_VOICEMAILS_CLEAR_ALL}?${qs.stringify(
        { room_id: roomId, pbx_id: pbxId },
        { skipNull: true, skipEmptyString: true },
      )}`
      return mango.post<ClearAllVoicemailsResult>(url)
    },

    deleteVoicemail: async (id: number): Promise<ApiResponse<void>> => {
      return mango.delete<void>(NON_PROXIED_ENDPOINTS.VOICEMAIL_DETAIL(id))
    },

    // --- PBX & Settings (hotels-service via api-gateway) ---

    /**
     * PBX hospitality settings. `_legacyPbxId` is unused here but is part of
     * the signature so consumers that only hold a numeric id (admin3, which
     * reads PBX detail through app v2) can override this method.
     */
    getPbx: async (
      pbxUuid: string,
      _legacyPbxId?: number,
    ): Promise<ApiResponse<PbxHospitalitySettings>> => {
      const response = await gateway.get<GatewayPbx>(
        HOTELS_GATEWAY_ENDPOINTS.PBX_DETAIL(pbxUuid),
      )
      if (!response.ok)
        return response as unknown as ApiResponse<PbxHospitalitySettings>
      const data = response.data as GatewayPbx
      return {
        ...response,
        data: {
          id: data.legacy_id ?? 0,
          uuid: data.uuid,
          name: data.name,
          industry_type: data.industry_type,
          xml_rebuilt_at: data.reset_schedule ?? null,
        },
      } as ApiResponse<PbxHospitalitySettings>
    },

    /**
     * Daily room reset time. hotels-service stores this as `reset_schedule` on
     * the PBX; the frontend keeps the legacy `xml_rebuilt_at` name.
     */
    setRoomResetSchedule: async (
      data: RoomResetSchedulePayload,
    ): Promise<ApiResponse<RoomResetScheduleResult>> => {
      const body: GatewayUpdatePbxRequest = {
        reset_schedule: data.xml_rebuilt_at,
      }
      const response = await gateway.patch<GatewayPbx>(
        HOTELS_GATEWAY_ENDPOINTS.PBX_DETAIL(data.pbx_uuid),
        body,
      )
      if (!response.ok)
        return response as unknown as ApiResponse<RoomResetScheduleResult>
      const pbx = response.data as GatewayPbx | undefined
      return {
        ...response,
        data: {
          pbx_id: pbx?.legacy_id ?? data.pbx_id,
          xml_rebuilt_at: pbx?.reset_schedule ?? data.xml_rebuilt_at,
        },
      } as ApiResponse<RoomResetScheduleResult>
    },

    // --- Extension Lines (mango-api only — no hotels-service equivalent) ---

    listExtensionLines: async (
      pbxId: number,
    ): Promise<ApiResponse<ExtensionLine[]>> => {
      return mango.get<ExtensionLine[]>(
        NON_PROXIED_ENDPOINTS.EXTENSION_LINES(pbxId),
      )
    },

    toggleExtensionHospitality: async (
      extensionId: number,
      hospitality_flag: boolean,
    ): Promise<ApiResponse<ExtensionLine>> => {
      return mango.patch<ExtensionLine>(
        NON_PROXIED_ENDPOINTS.EXTENSION_DETAIL(extensionId),
        { hospitality: hospitality_flag },
      )
    },

    bulkUpdateExtensionHospitality: async (
      extensionIds: number[],
      hospitality_flag: boolean,
    ): Promise<ApiResponse<BulkExtensionHospitalityUpdateResult>> => {
      return mango.patch<BulkExtensionHospitalityUpdateResult>(
        NON_PROXIED_ENDPOINTS.HOSPITALITY_EXTENSIONS,
        {
          extension_ids: extensionIds,
          hospitality: hospitality_flag,
        },
      )
    },
  }
}

export type HotelsApi = ReturnType<typeof createHotelsApi>
